<script setup lang="ts">
import { QEditor, QField, QInput, QItem, QItemSection } from 'quasar'
import { computed, nextTick, ref, useSlots, watch } from 'vue'
import { Color } from '@previo/codebooks'
import PIcon from '../icon/PIcon.vue'
import PTextDescription from '../typography/system/PTextDescription.vue'
import PCard from '../card/PCard.vue'
import { usePrevioDS } from '../../composables/helpers'
import type { PValidationRule } from '../../types/types'
import { $t } from '../../composables/i18n'
import { PBtnTableSize } from '../btn-table/types'
import { PCardSize } from '../card/types'
import PItem from '../list/PItem.vue'
import PList from '../list/PList.vue'
import PContextMenu from '../context-menu/PContextMenu.vue'
import PBtnTable from '../btn-table/PBtnTable.vue'
import {
  type Placeholder,
  PWysiwygTextareaEditorActions,
  PWysiwygTextInputFieldFormat,
} from './types'
import {
  htmlToMarkdown,
  markdownToHtml,
  sanitizeUrl,
  type WysiwygMarkdownOptions,
} from './composable/wysiwygMarkdown'
import LabelInputField from './components/LabelInputField.vue'

type Props = {
  label?: null | string
  tooltip?: null | string
  placeholder?: null | string
  hint?: null | string
  rules?: PValidationRule[]
  disable?: boolean
  placeholders?: Placeholder[]
  actions: PWysiwygTextareaEditorActions[]
  variableFormat?: PWysiwygTextInputFieldFormat
  cy: string
}

const model = defineModel<string | null>()

const props = withDefaults(defineProps<Props>(), {
  disable: false,
  variableFormat: PWysiwygTextInputFieldFormat.BRACKETS,
  rules() {
    return []
  },
  placeholders() {
    return []
  },
  actions() {
    return [PWysiwygTextareaEditorActions.PLACEHOLDERS]
  },
})

const ds = usePrevioDS()
const slots = useSlots()
const editorRef = ref<QEditor | null>(null)
const placeholderSearch = ref('')
const isOpen = ref<boolean>(false)

const toolbar = computed(() => {
  const actions = [...props.actions]
  if (actions.length === 0) {
    return []
  }
  if (slots['ds-append']) {
    actions.push('ds_append' as PWysiwygTextareaEditorActions)
  }
  return [actions]
})
const isInserting = ref(false)

const isRequired = computed(() => {
  return props.rules.includes(ds.rules.required)
})

const validationErrorMessage = computed<string | null>(() => {
  if (isRequired.value && !model.value) {
    const result = ds.rules.required(model.value)
    return typeof result === 'string' ? result : null
  }
  return null
})

const filteredPlaceholders = computed(() => {
  if (!placeholderSearch.value) {
    return props.placeholders
  }
  const search = placeholderSearch.value.toLowerCase()
  return props.placeholders.filter(
    (p) => p.key.toLowerCase().includes(search) || p.label.toLowerCase().includes(search),
  )
})

const getEditorEl = (): HTMLElement | null => {
  if (!editorRef.value) {
    return null
  }

  return (editorRef.value.$el as HTMLElement).querySelector(
    '.q-editor__content',
  ) as HTMLElement | null
}

const isInList = (el: Element | null | undefined): boolean => {
  return !!el && (!!el.closest('ul') || !!el.closest('ol') || !!el.closest('li'))
}

const getSelectionRangeInEditor = (editorEl: HTMLElement): Range => {
  const selection = window.getSelection()
  if (selection && selection.rangeCount > 0) {
    const r = selection.getRangeAt(0)
    if (editorEl.contains(r.commonAncestorContainer)) {
      return r
    }
  }
  const r = document.createRange()
  r.selectNodeContents(editorEl)
  r.collapse(false)
  return r
}

const markdownOptions = computed<WysiwygMarkdownOptions>(() => ({
  placeholders: props.placeholders,
  variableFormat: props.variableFormat,
}))

const internalValue = ref(markdownToHtml(model.value, markdownOptions.value))

watch(
  () => model.value,
  (val) => {
    if (isInserting.value) {
      return
    }
    const htmlVal = markdownToHtml(val, markdownOptions.value)
    if (
      htmlToMarkdown(htmlVal, markdownOptions.value) !==
      htmlToMarkdown(internalValue.value, markdownOptions.value)
    ) {
      internalValue.value = htmlVal
    }
  },
)

watch(internalValue, (val) => {
  model.value = htmlToMarkdown(val, markdownOptions.value) || null
})

watch(
  () => props.placeholders,
  () => {
    internalValue.value = markdownToHtml(model.value, markdownOptions.value)
  },
  { deep: true },
)

const onPlaceholderButtonClick = () => {
  isOpen.value = true
}

const addLink = () => {
  const url = prompt('Enter URL')
  if (!url || !editorRef.value) return
  editorRef.value.runCmd('createLink', sanitizeUrl(url))
}

const insertPlaceholder = (placeholder: Placeholder) => {
  isOpen.value = false

  if (!editorRef.value) {
    return
  }

  nextTick(() => {
    editorRef.value!.focus()

    const editorEl = getEditorEl()
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}`

    if (!editorEl) {
      const chip = createChip(placeholder, uid)
      editorRef.value!.runCmd('insertHTML', chip.outerHTML)
      placeholderSearch.value = ''
      return
    }

    const range = getSelectionRangeInEditor(editorEl)

    const chip = createChip(placeholder, uid)
    range.deleteContents()
    range.insertNode(chip)

    let space: Node | null = chip.nextSibling
    if (!space || space.nodeType !== Node.TEXT_NODE || (space.textContent ?? '') === '') {
      space = document.createTextNode('\u00A0')
      range.setStartAfter(chip)
      range.insertNode(space)
    }
    range.setStartAfter(space)
    range.collapse(true)

    isInserting.value = true
    internalValue.value = editorEl.innerHTML
    placeholderSearch.value = ''

    nextTick(() => {
      const selection = window.getSelection()
      const freshEditorEl = getEditorEl()

      if (freshEditorEl) {
        const freshChip = freshEditorEl.querySelector(
          `.p-wysiwyg-placeholder-chip[data-uid="${uid}"]`,
        ) as HTMLElement | null
        if (freshChip) {
          const r = document.createRange()
          const sib = freshChip.nextSibling
          if (sib && sib.nodeType === Node.TEXT_NODE) {
            r.setStart(sib, (sib.textContent || '').length)
          } else {
            r.setStartAfter(freshChip)
          }
          r.collapse(true)
          if (selection) {
            selection.removeAllRanges()
            selection.addRange(r)
          }
        }
      }

      isInserting.value = false
      editorRef.value!.focus()
    })
  })
}

const createChip = (placeholder: Placeholder, uid: string) => {
  const chip = document.createElement('span')
  chip.className = 'p-wysiwyg-placeholder-chip'
  chip.contentEditable = 'false'
  chip.dataset.key = placeholder.key
  chip.dataset.uid = uid
  chip.innerText =
    props.variableFormat === PWysiwygTextInputFieldFormat.CURLY
      ? `{${placeholder.label}}`
      : `[[${placeholder.label}]]`
  return chip
}

const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      const container = range.commonAncestorContainer
      const element =
        container.nodeType === Node.ELEMENT_NODE
          ? (container as HTMLElement)
          : container.parentElement

      if (isInList(element)) {
        return
      }

      e.preventDefault()
      document.execCommand('insertLineBreak')

      const editorEl = getEditorEl()
      if (editorEl) {
        nextTick(() => {
          internalValue.value = editorEl.innerHTML
        })
      }
      return
    }
  }

  if (e.key === 'Backspace') {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      if (range.collapsed) {
        const node: Node | null = range.startContainer
        const offset = range.startOffset

        let prev = null
        if (node.nodeType === Node.TEXT_NODE && offset === 0) {
          prev = node.previousSibling
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          prev = node.childNodes[offset - 1]
        }

        if (
          prev &&
          prev.nodeType === Node.ELEMENT_NODE &&
          (prev as HTMLElement).classList.contains('p-wysiwyg-placeholder-chip')
        ) {
          e.preventDefault()

          const editorEl = getEditorEl()
          if (!editorEl) {
            prev.remove()
            return
          }

          const marker = document.createElement('span')
          marker.id = 'caret-restore-marker'
          prev.parentNode?.insertBefore(marker, prev.nextSibling)

          prev.remove()

          isInserting.value = true
          internalValue.value = editorEl.innerHTML

          nextTick(() => {
            const freshEditorEl = getEditorEl()
            if (freshEditorEl) {
              const restoredMarker = freshEditorEl.querySelector('#caret-restore-marker')
              if (restoredMarker) {
                const r = document.createRange()
                r.setStartBefore(restoredMarker)
                r.collapse(true)
                const sel = window.getSelection()
                if (sel) {
                  sel.removeAllRanges()
                  sel.addRange(r)
                }
                restoredMarker.remove()
              }
            }
            isInserting.value = false
            editorRef.value?.focus()
          })
          return
        }
      }
    }
  }
}
</script>

<template>
  <div class="p-wysiwyg-textarea-field">
    <label-input-field :label="label" :is-required="isRequired" :tooltip="tooltip" />

    <q-field
      v-model="model"
      :rules="rules"
      :error="validationErrorMessage != null"
      :error-message="validationErrorMessage ?? undefined"
      :bg-color="Color.WHITE"
      :disable="disable"
      borderless
      dense
      no-error-icon
      hide-bottom-space
      class="p-full-width"
    >
      <template #control>
        <q-editor
          ref="editorRef"
          v-model="internalValue"
          :disable="disable"
          :placeholder="placeholder ?? undefined"
          :toolbar="toolbar"
          :toolbar-bg="Color.GREY_200"
          toolbar-rounded
          min-height="0"
          :data-cy="cy"
          class="p-full-width"
          @keydown="onKeydown"
        >
          <template #wysiwyg_link>
            <p-btn-table
              v-if="actions.includes(PWysiwygTextareaEditorActions.LINK)"
              icon="link"
              :cy="PWysiwygTextareaEditorActions.LINK"
              :color="Color.TRANSPARENT"
              :size="ds.screen().isMobile ? PBtnTableSize.MEDIUM : PBtnTableSize.SMALL"
              @click="addLink"
            />
          </template>
          <template #wysiwyg_ordered>
            <p-btn-table
              v-if="actions.includes(PWysiwygTextareaEditorActions.ORDERED_LIST)"
              icon="list-ol"
              :cy="PWysiwygTextareaEditorActions.ORDERED_LIST"
              :size="ds.screen().isMobile ? PBtnTableSize.MEDIUM : PBtnTableSize.SMALL"
              :color="Color.TRANSPARENT"
              @click="editorRef?.runCmd('insertOrderedList')"
            />
          </template>
          <template #wysiwyg_unordered>
            <p-btn-table
              v-if="actions.includes(PWysiwygTextareaEditorActions.UNORDERED_LIST)"
              icon="list-ul"
              :cy="PWysiwygTextareaEditorActions.UNORDERED_LIST"
              :color="Color.TRANSPARENT"
              :size="ds.screen().isMobile ? PBtnTableSize.MEDIUM : PBtnTableSize.SMALL"
              @click="editorRef?.runCmd('insertUnorderedList')"
            />
          </template>
          <template #wysiwyg_underline>
            <p-btn-table
              v-if="actions.includes(PWysiwygTextareaEditorActions.UNDERLINE)"
              icon="underline"
              :cy="PWysiwygTextareaEditorActions.UNDERLINE"
              :color="Color.TRANSPARENT"
              :size="ds.screen().isMobile ? PBtnTableSize.MEDIUM : PBtnTableSize.SMALL"
              @click="editorRef?.runCmd('underline')"
            />
          </template>
          <template #wysiwyg_italic>
            <p-btn-table
              v-if="actions.includes(PWysiwygTextareaEditorActions.ITALIC)"
              icon="italic"
              :cy="PWysiwygTextareaEditorActions.ITALIC"
              :color="Color.TRANSPARENT"
              :size="ds.screen().isMobile ? PBtnTableSize.MEDIUM : PBtnTableSize.SMALL"
              @click="editorRef?.runCmd('italic')"
            />
          </template>
          <template #wysiwyg_bold>
            <p-btn-table
              v-if="actions.includes(PWysiwygTextareaEditorActions.BOLD)"
              icon="bold"
              :cy="PWysiwygTextareaEditorActions.BOLD"
              :color="Color.TRANSPARENT"
              :size="ds.screen().isMobile ? PBtnTableSize.MEDIUM : PBtnTableSize.SMALL"
              @click="editorRef?.runCmd('bold')"
            />
          </template>
          <template #wysiwyg_placeholder_dropdown>
            <p-btn-table
              v-if="
                placeholders.length > 0 &&
                actions.includes(PWysiwygTextareaEditorActions.PLACEHOLDERS)
              "
              icon="brackets-curly"
              :cy="PWysiwygTextareaEditorActions.PLACEHOLDERS"
              :color="Color.TRANSPARENT"
              :size="ds.screen().isMobile ? PBtnTableSize.MEDIUM : PBtnTableSize.SMALL"
              @click="onPlaceholderButtonClick"
            >
              <p-context-menu
                v-model="isOpen"
                cy="p-wysiwyg-placeholder-menu"
                :title="$t('Placeholders')"
              >
                <q-input v-model="placeholderSearch" outlined autofocus class="q-mb-sm">
                  <template #append>
                    <p-icon icon="search" />
                  </template>
                </q-input>
                <p-list>
                  <p-item
                    v-for="item in filteredPlaceholders"
                    :key="item.key"
                    :text="item.label"
                    :cy="`placeholder-parameter-${item.key}`"
                    @click="insertPlaceholder(item)"
                  />
                  <q-item v-if="filteredPlaceholders.length === 0">
                    <q-item-section class="text-grey">No matches found</q-item-section>
                  </q-item>
                </p-list>
              </p-context-menu>
            </p-btn-table>
          </template>
          <template v-if="$slots['ds-append']" #ds_append>
            <div class="ds-append-toolbar">
              <slot name="ds-append" />
            </div>
          </template>
        </q-editor>
      </template>
    </q-field>

    <p-card v-if="hint && !validationErrorMessage" :pa-mobile="PCardSize.XS">
      <p-text-description :text="hint" :color="Color.GREY_500" />
    </p-card>
  </div>
</template>

<style scoped lang="scss">
@import '../../assets/scss/shared/variables/ds.radius';
@import '../../assets/scss/shared/brand.module';

.p-wysiwyg-textarea-field {
  $editor-line-height: 1.5;
  $editor-visible-lines: 5;
  $editor-content-padding: 20px;

  display: flex;
  flex-direction: column;

  :deep(.q-field) {
    flex: 1 1 auto;
    min-height: 0;
  }

  :deep(.q-field__inner),
  :deep(.q-field__control),
  :deep(.q-field__control-container),
  :deep(.q-field__native),
  :deep(.q-editor) {
    height: 100%;
    min-height: 0;
  }

  :deep(.q-field__native) {
    padding: 0;
  }

  :deep(.q-editor) {
    border-color: v-bind('validationErrorMessage ? "var(--q-negative)" : "rgba(0, 0, 0, 0.24)"');
    border-radius: 10px;

    display: flex;
    flex-direction: column;

    .q-editor__content {
      flex: 1 1
        calc(#{$editor-line-height}em * #{$editor-visible-lines} + #{$editor-content-padding});
      min-height: 0;
      line-height: $editor-line-height;
      overflow-y: auto;
    }

    .q-editor__toolbars-container > div:first-child {
      border-radius: 10px;
      margin: 1px;
    }

    .p-wysiwyg-placeholder-chip {
      display: inline-block;
      background-color: getColor('grey100');
      border: 1px solid getColor('grey400');
      border-radius: $radius-md-mobile;
      padding: 6px 4px;
      margin: 0 2px;
      font-weight: 400;
      color: getColor('black');
      user-select: none;
      vertical-align: baseline;
      line-height: 10px;
    }

    a {
      color: blue;
      text-decoration: underline;
    }
  }

  :deep(.q-editor__toolbar-group) {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    flex: 1;
  }

  :deep(.ds-append-toolbar) {
    margin-left: auto;
  }
}
</style>
